import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, BACKEND_URL } from './api';

const SiteSettingsContext = createContext(null);

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loaded, setLoaded] = useState(false);

  const [logoVersion, setLogoVersion] = useState(Date.now());

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/site-settings');
      setSettings(res.data || {});
      setLogoVersion(Date.now()); // bust cache on refresh
    } catch {
      // non-fatal — use defaults
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Computed helpers — return null while loading to prevent stock photo flash
  const logoUrl = !loaded ? null : settings.logo_stored
    ? `${BACKEND_URL}/api/site-settings/image/logo?v=${logoVersion}`
    : null;

  const navIconUrl = !loaded ? null : settings.nav_icon_stored
    ? `${BACKEND_URL}/api/site-settings/image/nav_icon?v=${logoVersion}`
    : logoUrl;

  const heroImageUrl = !loaded ? null : settings.hero_image_stored
    ? `${BACKEND_URL}/api/site-settings/image/hero_image`
    : (settings.hero_image_url || null);

  const infoImageUrl = !loaded ? null : settings.info_image_stored
    ? `${BACKEND_URL}/api/site-settings/image/info_image`
    : (settings.info_image_url || null);

  const heroImage2Url = !loaded ? null : settings.hero_image_2_stored
    ? `${BACKEND_URL}/api/site-settings/image/hero_image_2`
    : (settings.hero_image_2_url || null);

  const guidePdfUrl = !loaded ? null : settings.guide_pdf_stored
    ? `${BACKEND_URL}/api/site-settings/image/guide_pdf`
    : null;

  const tripsBgUrl = !loaded ? null : settings.trips_bg_stored
    ? `${BACKEND_URL}/api/site-settings/image/trips_bg`
    : (settings.trips_bg_url || null);

  const statsBgUrl = !loaded ? null : settings.stats_bg_stored
    ? `${BACKEND_URL}/api/site-settings/image/stats_bg`
    : null;

  const infoDescription = settings.info_description || 
    'Join our volunteer medical teams providing essential surgical procedures to patients in Guatemala who cannot afford the care they desperately need.';

  const infoAboutText = settings.info_about_text ||
    'Guatemala Surgery has operated since 1988, sending volunteer teams of surgeons, anesthesiologists, nurses, and support staff to Antigua each October.';

  const orgName = settings.org_name || 'Guatemala Surgery';

  // Email templates
  const declineEmailA = {
    subject: settings.decline_email_a_subject || 'Thank you for your interest – Guatemala Surgery Mission',
    body: settings.decline_email_a_body || '',
  };
  const declineEmailB = {
    subject: settings.decline_email_b_subject || 'Regarding your Guatemala Surgery Mission application',
    body: settings.decline_email_b_body || '',
  };

  // Info box texts (admin-editable notification boxes)
  const infoBoxes = {
    flight: settings.info_box_flight || '',
    hotel: settings.info_box_hotel || '',
    license: settings.info_box_license || '',
  };

  // License required roles (admin-configurable)
  const licenseRequiredRoles = settings.license_required_roles || ['GYN Surgeon', 'General Surgeon', 'Subspecialty Surgeon', 'Anesthesiologist'];

  // Donation settings
  const donationUrl = settings.donation_url || 'https://give.commonhope.org/campaign/guatemala-surgery/c222592';
  const donationNote = settings.donation_note || '';

  return (
    <SiteSettingsContext.Provider value={{
      settings, loaded, fetchSettings,
      logoUrl, navIconUrl, heroImageUrl, heroImage2Url, infoImageUrl, guidePdfUrl, tripsBgUrl, statsBgUrl,
      infoDescription, infoAboutText, orgName,
      declineEmailA, declineEmailB, infoBoxes, licenseRequiredRoles,
      donationUrl, donationNote
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
